import React, { useState, createContext, useContext, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { INDIAN_STATES, PRIORITIES, STATUS_CONFIG } from './data/mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============ Auth Context ============
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nicobar_user')); } catch { return null; }
  });

  const login = async (email, password) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/nicobar/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.detail || 'Invalid credentials' };
      setUser(data.user);
      localStorage.setItem('nicobar_user', JSON.stringify(data.user));
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = () => { setUser(null); localStorage.removeItem('nicobar_user'); localStorage.removeItem('nicobar_client_name'); localStorage.removeItem('nicobar_client_code'); };

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
};

// ============ Ticket Context ============
const TicketContext = createContext(null);
export const useTickets = () => useContext(TicketContext);

const TicketProvider = ({ children }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchTickets = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/nicobar/tickets`;
      const params = new URLSearchParams();
      if (user.role === 'store') { params.set('role', 'store'); params.set('userId', user.id); }
      else if (user.role === 'vendor') { params.set('role', 'vendor'); params.set('vendorId', user.vendorId); }
      if (params.toString()) url += `?${params}`;
      const res = await fetch(url);
      if (res.ok) { const data = await res.json(); setTickets(data); }
    } catch (err) { console.error('Failed to fetch tickets:', err); }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const createTicket = async (data) => {
    const res = await fetch(`${BACKEND_URL}/api/nicobar/tickets`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create ticket');
    const ticket = await res.json();
    setTickets(prev => [ticket, ...prev]);
    return ticket;
  };

  const updateTicket = async (id, updates, logEntry) => {
    const res = await fetch(`${BACKEND_URL}/api/nicobar/tickets/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates, logEntry })
    });
    if (!res.ok) throw new Error('Failed to update ticket');
    const updated = await res.json();
    setTickets(prev => prev.map(t => t.id === id ? updated : t));
    return updated;
  };

  const refreshTickets = fetchTickets;

  return <TicketContext.Provider value={{ tickets, loading, createTicket, updateTicket, refreshTickets }}>{children}</TicketContext.Provider>;
};

// ============ Notification Context ============
const NotificationContext = createContext(null);
export const useNotifications = () => useContext(NotificationContext);

const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const { user } = useAuth();

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/nicobar/notifications/${user.id}`);
      if (res.ok) setNotifications(await res.json());
    } catch (err) { console.error('Notif fetch error:', err); }
  }, [user]);

  useEffect(() => { fetchNotifications(); const interval = setInterval(fetchNotifications, 30000); return () => clearInterval(interval); }, [fetchNotifications]);

  const markRead = async (notifId) => {
    await fetch(`${BACKEND_URL}/api/nicobar/notifications/${notifId}/read`, { method: 'PUT' });
    setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
  };

  const markAllRead = async () => {
    if (!user) return;
    await fetch(`${BACKEND_URL}/api/nicobar/notifications/read-all/${user.id}`, { method: 'PUT' });
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return <NotificationContext.Provider value={{ notifications, unreadCount, markRead, markAllRead, refreshNotifications: fetchNotifications }}>{children}</NotificationContext.Provider>;
};

// ============ Protected Route ============
const ProtectedRoute = ({ children, roles }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/nicobar/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/nicobar/dashboard" replace />;
  return children;
};

// ============ Lazy Imports ============
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const DashboardLayout = React.lazy(() => import('./components/layout/DashboardLayout'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const TicketList = React.lazy(() => import('./pages/TicketList'));
const CreateTicket = React.lazy(() => import('./pages/CreateTicket'));
const TicketDetail = React.lazy(() => import('./pages/TicketDetail'));
const ManagementPage = React.lazy(() => import('./pages/ManagementPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));

const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen bg-slate-50">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-3 border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-gray-500">Loading...</span>
    </div>
  </div>
);

export default function NicobarApp() {
  return (
    <AuthProvider>
      <TicketProvider>
        <NotificationProvider>
          <React.Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="login" element={<LoginPage />} />
              <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="tickets" element={<TicketList />} />
                <Route path="tickets/create" element={<ProtectedRoute roles={['store']}><CreateTicket /></ProtectedRoute>} />
                <Route path="tickets/:id" element={<TicketDetail />} />
                <Route path="reports" element={<ProtectedRoute roles={['admin', 'superadmin']}><ReportsPage /></ProtectedRoute>} />
                <Route path="management/:type" element={<ProtectedRoute roles={['superadmin']}><ManagementPage /></ProtectedRoute>} />
              </Route>
              <Route path="*" element={<Navigate to="/nicobar/login" replace />} />
            </Routes>
          </React.Suspense>
        </NotificationProvider>
      </TicketProvider>
    </AuthProvider>
  );
}
